import type { ReactNode } from 'react';
import { cn } from './cn.js';

export interface StepDef {
  key: string;
  label: ReactNode;
  /** Zem soļa (laikspiedols, aktors, artefakts). */
  meta?: ReactNode;
  state: 'done' | 'current' | 'upcoming' | 'blocked';
}

const dot: Record<StepDef['state'], string> = {
  done: 'bg-brand-600 text-white',
  current: 'bg-amber-500 text-white ring-4 ring-amber-500/20',
  upcoming: 'bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-400',
  blocked: 'bg-red-100 text-red-600 ring-2 ring-red-500/40 dark:bg-red-950',
};

/**
 * Horizontāls dzīves cikla stepperis (ritināms šaurākos ekrānos).
 * Ģenērisks — domēna kartēšanu (statusi/artefakti) veido izsaucējs.
 */
export function Stepper({ steps, className }: { steps: StepDef[]; className?: string }) {
  return (
    <ol className={cn('flex overflow-x-auto pb-2', className)}>
      {steps.map((s, i) => (
        <li key={s.key} className="flex min-w-[7.5rem] flex-1 flex-col">
          <div className="flex items-center">
            <div
              className={cn(
                'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold',
                dot[s.state],
              )}
              aria-current={s.state === 'current' ? 'step' : undefined}
            >
              {s.state === 'done' ? '✓' : i + 1}
            </div>
            {i < steps.length - 1 ? (
              <div
                className={cn(
                  'mx-2 h-0.5 flex-1 rounded',
                  s.state === 'done' ? 'bg-brand-500' : 'bg-slate-200 dark:bg-slate-700',
                )}
              />
            ) : null}
          </div>
          <div className="mt-2 pr-3">
            <p
              className={cn(
                'text-xs font-semibold',
                s.state === 'current'
                  ? 'text-amber-600 dark:text-amber-400'
                  : s.state === 'done'
                    ? 'text-slate-900 dark:text-white'
                    : 'text-slate-500 dark:text-slate-400',
              )}
            >
              {s.label}
            </p>
            {s.meta ? (
              <p className="mt-0.5 text-[11px] leading-tight text-slate-400 dark:text-slate-500">
                {s.meta}
              </p>
            ) : null}
          </div>
        </li>
      ))}
    </ol>
  );
}
